import { useState, useEffect } from 'react';
import { getCurrentProductionMonth } from '@/utils/productionPeriod';
import { useProductionStartDay } from '@/hooks/useProductionStartDay';
import { useConfiguracoes } from '@/features/configuracoes/hooks/useConfiguracoes';

const STORAGE_KEY = 'producao_periodo_filtro';

function getStoredPeriod(): { month: number; year: number } | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (typeof parsed.month === 'number' && typeof parsed.year === 'number') {
      return parsed;
    }
    return null;
  } catch {
    return null;
  }
}

function storePeriod(period: { month: number; year: number } | null) {
  try {
    if (period) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(period));
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  } catch {
    // Silenciar erros de localStorage
  }
}

/**
 * Hook que gerencia o filtro de período de produção com persistência no localStorage.
 * 
 * - Na primeira visita (sem nada salvo), carrega o período de produção atual.
 * - Persiste a seleção do usuário entre navegações/reloads.
 * 
 * @param shouldAutoInit - Se true (padrão), inicializa automaticamente com o período
 *   de produção atual. Se false, inicia sem filtro (null) para mostrar todos os registros.
 *   Útil para roles que não trabalham com ciclo de produção (ex: Pós-Venda, Secretária).
 */
export function useProductionPeriodFilter(shouldAutoInit: boolean = true) {
  const startDay = useProductionStartDay();
  const { state: { tenantSettings } } = useConfiguracoes();

  const [productionPeriodFilter, _setProductionPeriodFilter] = useState<{ month: number; year: number } | null>(() => {
    // Se não deve auto-inicializar, começa sem filtro
    if (!shouldAutoInit) return null;

    // Tentar restaurar do localStorage
    const stored = getStoredPeriod();
    if (stored) return stored;

    // Fallback: período atual (será recalculado quando startDay estiver disponível)
    return null;
  });

  // Quando startDay carregar (configurações assíncronas), inicializar com período atual se ainda não houver nada
  // Apenas se shouldAutoInit for true
  useEffect(() => {
    if (shouldAutoInit && productionPeriodFilter === null && tenantSettings) {
      const initialPeriod = getCurrentProductionMonth(startDay);
      _setProductionPeriodFilter(initialPeriod);
      storePeriod(initialPeriod);
    }
  }, [tenantSettings, startDay, shouldAutoInit]);

  const setProductionPeriodFilter = (period: { month: number; year: number } | null) => {
    _setProductionPeriodFilter(period);
    storePeriod(period);
  };

  return {
    productionPeriodFilter,
    setProductionPeriodFilter,
    startDay,
  };
}
